import React, { useState, useEffect } from "react";
import styled from "styled-components";

const ContactPageWrapper = styled.div`
  width: 100%;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #333 0%, #222 100%);
  color: white;
  padding: 80px 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 900;
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 20px;
  margin: 0 auto;
  opacity: 0.9;
  max-width: 600px;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;

  @media (max-width: 1024px) {
    gap: 60px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactForm = styled.form`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 30px;
  }
`;

const FormTitle = styled.h2`
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const FormDescription = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 30px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6720;
  }

  &::placeholder {
    color: #999;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6720;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.3s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #ff6720;
  }

  &::placeholder {
    color: #999;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 15px 24px;
  background: #ff6720;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #e55a1c;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ContactInfo = styled.div``;

const InfoSection = styled.div`
  margin-bottom: 40px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoTitle = styled.h3`
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const InfoContent = styled.div`
  font-size: 16px;
  color: #666;
  line-height: 1.6;

  p {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: #ff6720;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const ContactCard = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const ContactIcon = styled.div`
  width: 60px;
  height: 60px;
  background: #ff6720;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 24px;
  color: white;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
`;

const CardTitle = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
`;

const CardContent = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const FAQSection = styled.section`
  background: #f8f9fa;
  padding: 80px 0;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: bold;
  color: #333;
  margin-bottom: 50px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FAQItem = styled.div`
  background: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const FAQQuestion = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
`;

const FAQAnswer = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
`;

const ContactPage = () => {
  useEffect(() => {
    document.title = "Contact Us | XPLOD-GROUPS";
    return () => {
      document.title = "Home | XPLOD-GROUPS";
    };
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get API URL from environment or use default
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        "https://xplod-group-react.ak2008.workers.dev";

      const response = await fetch(`${apiUrl}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowSuccess(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });

        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      } else {
        alert(data.error || "Failed to submit form. Please try again.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      alert(
        "Failed to submit form. Please check your connection and try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactPageWrapper>
      <HeroSection>
        <div className="container">
          <HeroTitle>Contact Us</HeroTitle>
          <HeroSubtitle>
            We're here to help! Get in touch with our customer service team for
            support, questions, or feedback.
          </HeroSubtitle>
        </div>
      </HeroSection>

      <ContentSection>
        <div className="container">
          <ContactGrid>
            <ContactForm onSubmit={handleSubmit}>
              <FormTitle>Send us a Message</FormTitle>
              <FormDescription>
                Fill out the form below and we'll get back to you as soon as
                possible.
              </FormDescription>

              {showSuccess && (
                <SuccessMessage>
                  Thank you for your message! We'll get back to you within 24
                  hours.
                </SuccessMessage>
              )}

              <FormGrid>
                <FormGroup>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    required
                  />
                </FormGroup>
              </FormGrid>

              <FormGrid>
                <FormGroup>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="subject">Subject *</Label>
                  <Select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a subject</option>
                    <option value="product-support">Product Support</option>
                    <option value="warranty">Warranty Claim</option>
                    <option value="parts">Replacement Parts</option>
                    <option value="general">General Inquiry</option>
                    <option value="complant">Complant</option>
                    <option value="feedback">Feedback</option>
                    <option value="other">Other</option>
                  </Select>
                </FormGroup>
              </FormGrid>

              <FormGroup className="full-width">
                <Label htmlFor="message">Message *</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Please describe your question or concern in detail..."
                  required
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Message"}
              </SubmitButton>
            </ContactForm>

            <ContactInfo>
              <InfoSection>
                <InfoTitle>Customer Service</InfoTitle>
                <InfoContent>
                  <p>
                    <strong>Phone:</strong>{" "}
                    <a href="tel:+18005446986">1-800-544-6986</a>
                  </p>
                  <p>
                    <strong>Hours:</strong> Monday - Saturday, 8.30AM - 7.00PM
                  </p>
                  <p>
                    <strong>Email:</strong>{" "}
                    <a href="mailto:xplodmarketing@gmail.com">
                      xplodmarketing@gmail.com
                    </a>
                  </p>
                </InfoContent>
              </InfoSection>

              <ContactCard>
                <ContactIcon>📞</ContactIcon>
                <CardTitle>Phone Support</CardTitle>
                <CardContent>
                  Call us for immediate assistance with your BLACK+DECKER
                  products
                </CardContent>
              </ContactCard>

              <ContactCard>
                <ContactIcon>💬</ContactIcon>
                <CardTitle>Live Chat</CardTitle>
                <CardContent>
                  Chat with our support team during business hours
                </CardContent>
              </ContactCard>

              <ContactCard>
                <ContactIcon>📧</ContactIcon>
                <CardTitle>Email Support</CardTitle>
                <CardContent>
                  Send us an email and we'll respond within 24 hours
                </CardContent>
              </ContactCard>

              <InfoSection>
                <InfoTitle>Headquarters</InfoTitle>
                <InfoContent>
                  <p>
                    Stanley Black & Decker
                    <br />
                    701 East Joppa Road
                    <br />
                    Towson, MD 21286
                    <br />
                    United States
                  </p>
                </InfoContent>
              </InfoSection>

              <InfoSection>
                <InfoTitle>Warranty & Returns</InfoTitle>
                <InfoContent>
                  <p>
                    For warranty claims and product returns, please have your
                    model number and purchase receipt ready.
                  </p>
                  <p>
                    <a href="/warranty">View Warranty Information</a>
                  </p>
                  <p>
                    <a href="/returns">Return Policy</a>
                  </p>
                </InfoContent>
              </InfoSection>
            </ContactInfo>
          </ContactGrid>
        </div>
      </ContentSection>

      <FAQSection>
        <div className="container">
          <SectionTitle>Frequently Asked Questions</SectionTitle>
          <FAQGrid>
            <FAQItem>
              <FAQQuestion>
                How do I register my product for warranty?
              </FAQQuestion>
              <FAQAnswer>
                You can register your BLACK+DECKER product online through our
                warranty registration page. Have your model number and purchase
                date ready.
              </FAQAnswer>
            </FAQItem>

            <FAQItem>
              <FAQQuestion>Where can I find replacement parts?</FAQQuestion>
              <FAQAnswer>
                Replacement parts are available through our parts department.
                Call customer service or visit our parts catalog online with
                your model number.
              </FAQAnswer>
            </FAQItem>

            <FAQItem>
              <FAQQuestion>What is your return policy?</FAQQuestion>
              <FAQAnswer>
                We offer a 30-day return policy for unused products in original
                packaging. Returns must include original receipt and all
                accessories.
              </FAQAnswer>
            </FAQItem>

            <FAQItem>
              <FAQQuestion>
                How long is the warranty on BLACK+DECKER products?
              </FAQQuestion>
              <FAQAnswer>
                Warranty periods vary by product category. Most power tools have
                a 2-year warranty, while outdoor products typically have a
                3-year warranty.
              </FAQAnswer>
            </FAQItem>

            <FAQItem>
              <FAQQuestion>Can I get my product repaired?</FAQQuestion>
              <FAQAnswer>
                Yes, we offer repair services through authorized service
                centers. Contact customer service to find the nearest location
                and get a repair estimate.
              </FAQAnswer>
            </FAQItem>

            <FAQItem>
              <FAQQuestion>Do you offer technical support?</FAQQuestion>
              <FAQAnswer>
                Our technical support team can help with product operation,
                troubleshooting, and maintenance questions during business
                hours.
              </FAQAnswer>
            </FAQItem>
          </FAQGrid>
        </div>
      </FAQSection>
    </ContactPageWrapper>
  );
};

export default ContactPage;
